import { createStore } from "vuex";
import loadingModule from "./modules/loadingModule.js";
import router from "../router";
import cookies from "vue-cookies";
import authService from "../services/authService.js";
import dataService from "../services/dataService.js";
import swal from 'sweetalert2';

const asIsinShare = (payload) => {
  if (payload == null || payload === "") return null;
  if (typeof payload !== "object") {
    return { ISINID: payload, ISIN: "", Nm: "", Clr: "" };
  }
  const id = payload.ISINID || payload.id || payload.Isinid;
  if (id == null || String(id).trim() === "") return null;
  return {
    ISINID: id,
    ISIN: payload.ISIN || payload.isin || "",
    Nm: payload.Nm || payload.ISINName || payload.Name || payload.title || "",
    Clr: payload.Clr || payload.Color || payload.ISINColor || ""
  };
};

const OVERLAY_Z_MIN = 2100;
const OVERLAY_Z_MAX = 2580;

const bringOverlayToFront = (state, key) => {
  let next = Number(state.overlaySeq) || OVERLAY_Z_MIN;
  next += 1;
  if (next < OVERLAY_Z_MIN + 1) next = OVERLAY_Z_MIN + 1;
  if (next >= OVERLAY_Z_MAX) next = OVERLAY_Z_MIN + 1;
  state.overlaySeq = next;
  if (key === "forum") state.forum.zIndex = next;
  if (key === "chart") state.isinChartModal.zIndex = next;
};

const DARK_MODE_KEY = "inga_dark_mode";
const DARK_MODE_EXPIRE = "1y";

const isDarkFlag = (value) => value === true || value === "true" || value === 1 || value === "1";

const isDarkSettingItem = (item) => String(item?.UserSettingType || "").toLowerCase() === "dark mode";

const parseDarkSettingValue = (raw) => {
  const value = String(raw ?? "").trim().toUpperCase();
  if (value === "Y" || value === "TRUE" || value === "1") return true;
  if (value === "N" || value === "FALSE" || value === "0") return false;
  return null;
};

const readDarkMode = () => {
  if (typeof window === "undefined") return true;
  if (cookies.isKey(DARK_MODE_KEY)) return isDarkFlag(cookies.get(DARK_MODE_KEY));
  try {
    const fromLs = localStorage.getItem(DARK_MODE_KEY);
    if (fromLs === "true" || fromLs === "false") return fromLs === "true";
  } catch (e) {
    /* ignore private mode */
  }
  return true;
};

const writeDarkMode = (isDark) => {
  const value = isDark ? "true" : "false";
  cookies.set(DARK_MODE_KEY, value, DARK_MODE_EXPIRE);
  try {
    localStorage.setItem(DARK_MODE_KEY, value);
  } catch (e) {
    /* ignore private mode */
  }
};

const initialDarkMode = readDarkMode();

export default createStore({
  state: {
    isPinned: true,
    sidebarType: "bg-white",
    isRTL: false,
    mcolor: "",
    darkMode: initialDarkMode,
    isDarkMode: initialDarkMode,
    darkSettingTypeId: null,
    isNavFixed: false,
    isAbsolute: false,
    showNavs: true,
    showSidenav: true,
    showNavbar: true,
    showFooter: false,
    showMain: true,
    showHelpDeskDialog: false,
    layout: "default",
    Notice: null,
    OpenGame: [],
    RunningGame: [],
    GameDetail: [],
    RewardDetail: [],
    ISINStatusInfo: [],
    ISINChartData: null,
    imageCacheKey: 1,
    modal: {
      title: '',
      view: '',
      isOpen: false,
      data: null,
    },
    alert: {
      type: null,
      message: null
    },
    /* ====== Auth =====  */
    token: '',
    user: null,
    showAuthModal: false,
    showForm: "",
    pendingAddToGame: null,
    gamesRefreshKey: 0,
    creditRefreshKey: 0,
    overlaySeq: 2100,
    forum: {
      isOpen: false,
      title: '',
      context: null,
      posts: [],
      loading: false,
      zIndex: 2250,
    },
    lightbox: {
      isOpen: false,
      src: '',
      alt: '',
      sources: [],
      index: 0,
    },
    isinChartModal: {
      isOpen: false,
      share: null,
      data: null,
      loading: false,
      zIndex: 2050,
    },
  },
  mutations: {
    navbarMinimize(state) {
      const sidenav_show = document.querySelector(".g-sidenav-show");

      if (sidenav_show.classList.contains("g-sidenav-pinned")) {
        //sidenav_show.classList.remove("g-sidenav-hidden");
        sidenav_show.classList.remove("g-sidenav-pinned");
        state.isPinned = false;
      } else {
        //sidenav_show.classList.add("g-sidenav-hidden");
        sidenav_show.classList.add("g-sidenav-pinned");
        state.isPinned = true;
      }
    },
    sidebarType(state, payload) {
      state.sidebarType = payload;
    },
    setDarkMode(state, isDark) {
      state.isDarkMode = !!isDark;
      state.darkMode = !!isDark;
      writeDarkMode(!!isDark);
    },
    setDarkSettingTypeId(state, typeId) {
      state.darkSettingTypeId = typeId || null;
    },
    navbarFixed(state) {
      if (state.isNavFixed === false) {
        state.isNavFixed = true;
      } else {
        state.isNavFixed = false;
      }
    },
    setNotice: (state, notice) => state.Notice = notice,
    setISINStatusInfo: (state, isinStatusInfo) => state.ISINStatusInfo = isinStatusInfo,
    setISINChartData: (state, isinChartData) => state.ISINChartData = isinChartData,
    setOpenGame: (state, openGame) => state.OpenGame = openGame,
    setOpenGameDetail: (state, gameDetail) => { state.GameDetail = gameDetail },
    setRunningGame: (state, runningGame) => state.RunningGame = runningGame,
    setRewardDetail: (state, rewardDetail) => { state.RewardDetail = rewardDetail },
    toggleHelpDeskDialog: (state) => state.showHelpDeskDialog= !state.showHelpDeskDialog,
    updateImageCacheKey: (state) => state.imageCacheKey = +new Date().getTime(),

    /* ============= Authentication ============= */
    toggleAuthModal (state) {
      state.showAuthModal = !state.showAuthModal;
    },
    SET_AUTH_MODAL (state, open) {
      state.showAuthModal = !!open;
    },
    OPEN_LOGIN_MODAL (state) {
      state.showAuthModal = true;
      state.showForm = "login";
    },
    toggleLoginForm (state) {
      state.showAuthModal = !state.showAuthModal;
      state.showForm = "login";
    },
    toggleRegisterForm (state) {
      state.showAuthModal = !state.showAuthModal;
      state.showForm = "register";
    },
    toggleForgotPasswordForm (state) {
      state.showAuthModal = !state.showAuthModal;
      state.showForm = "forgot-password";
    },
    toggleChangePasswordForm (state) {
      state.showAuthModal = !state.showAuthModal;
      state.showForm = "change-password";
    },
    SET_TOKEN: (state, token) => {
      state.token = token;
    },
    SET_USER: (state, user) => {
      state.user = user || null;
    },
    SET_PENDING_ADD_TO_GAME: (state, payload) => {
      state.pendingAddToGame = payload || null;
    },
    GAMES_REFRESH: (state) => {
      state.gamesRefreshKey += 1;
    },
    CREDIT_REFRESH: (state) => {
      state.creditRefreshKey += 1;
    },
    REMOVE_TOKEN: (state) => {
      state.token = '';
    },
    REMOVE_USER: (state) => {
      state.user = null;
      state.darkSettingTypeId = null;
    },
    /* ============= MODAL ============= */
    OPEN_MODAL: (state, payload) => { 
      if (payload.title) state.modal.title = payload.title;
      if (payload.view) state.modal.view = payload.view;
      state.modal.data = Object.prototype.hasOwnProperty.call(payload, 'data') ? payload.data : null;
      state.modal.isOpen = true;
    },
    CLOSE_MODAL: ( state ) => {
      state.modal.isOpen = false;
      if (state.modal.view === 'ISINChart') state.ISINChartData = null; 
      if (state.modal.view === 'GameDetail') state.GameDetail = []; 
      if (state.modal.view === 'RewardDetail') state.RewardDetail = []; 
      state.modal.title = '';
      state.modal.view = '';
      state.modal.data = null;
    },
    OPEN_FORUM: (state, payload) => {
      state.forum.isOpen = true;
      bringOverlayToFront(state, "forum");
      state.forum.title = payload?.title || 'Fórum';
      state.forum.context = {
        ISINID: payload?.ISINID || null,
        GameID: payload?.GameID || null,
        RewardID: payload?.RewardID || null,
        ISIN: payload?.ISIN || null,
        Img: payload?.Img || null,
      };
      state.forum.posts = [];
      state.forum.loading = true;
    },
    CLOSE_FORUM: (state) => {
      state.forum.isOpen = false;
      state.forum.title = '';
      state.forum.context = null;
      state.forum.posts = [];
      state.forum.loading = false;
    },
    OPEN_LIGHTBOX: (state, payload) => {
      const sources = Array.isArray(payload?.sources)
        ? payload.sources.filter((item) => !!item)
        : [];
      const src = payload?.src || sources[0] || '';
      const list = sources.length ? sources : (src ? [src] : []);
      let index = Number(payload?.index);
      if (!Number.isFinite(index) || index < 0 || index >= list.length) index = 0;
      state.lightbox.isOpen = list.length > 0;
      state.lightbox.src = list[index] || '';
      state.lightbox.alt = payload?.alt || 'Výhra';
      state.lightbox.sources = list;
      state.lightbox.index = index;
    },
    STEP_LIGHTBOX: (state, delta) => {
      const list = state.lightbox.sources || [];
      if (list.length < 2) return;
      const next = (state.lightbox.index + Number(delta) + list.length) % list.length;
      state.lightbox.index = next;
      state.lightbox.src = list[next] || '';
    },
    CLOSE_LIGHTBOX: (state) => {
      state.lightbox.isOpen = false;
      state.lightbox.src = '';
      state.lightbox.alt = '';
      state.lightbox.sources = [];
      state.lightbox.index = 0;
    },
    OPEN_ISIN_CHART_MODAL: (state, share) => {
      state.isinChartModal.isOpen = true;
      bringOverlayToFront(state, "chart");
      state.isinChartModal.share = share || null;
      state.isinChartModal.data = null;
      state.isinChartModal.loading = true;
    },
    SET_ISIN_CHART_MODAL: (state, payload) => {
      if (payload.share) state.isinChartModal.share = payload.share;
      if (Object.prototype.hasOwnProperty.call(payload, 'data')) {
        state.isinChartModal.data = payload.data;
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'loading')) {
        state.isinChartModal.loading = !!payload.loading;
      }
    },
    CLOSE_ISIN_CHART_MODAL: (state) => {
      state.isinChartModal.isOpen = false;
      state.isinChartModal.share = null;
      state.isinChartModal.data = null;
      state.isinChartModal.loading = false;
      state.ISINChartData = null;
    },
    SET_FORUM_LOADING: (state, loading) => {
      state.forum.loading = !!loading;
    },
    SET_FORUM_POSTS: (state, posts) => {
      state.forum.posts = Array.isArray(posts) ? posts : [];
      state.forum.loading = false;
    },
    /* ============= Alert ============= */
    alertSuccess(state, message) {
      state.alert.type = 'success';
      state.alert.message = message;
    },
    alertError(state, message) {
      state.alert.type = 'danger';
      state.alert.message = message;
    },
    alertClear(state) {
      setTimeout(() => {
        state.alert.type = null;
        state.alert.message = null;
      }, 3000);
    },
  },
  actions: {
    toggleSidebarColor({ commit }, payload) {
      commit("sidebarType", payload);
    },
    toggleAuthModal({ commit, state, getters }) {
      if (state.alert.message) commit('alertClear');
      const closing = state.showAuthModal;
      commit("toggleAuthModal");
      if (closing && !getters.isLoggedIn) {
        commit('SET_PENDING_ADD_TO_GAME', null);
      }
    },
    toggleHelpDeskDialog({ commit, state }) {
      if (state.alert.message) commit('alertClear');
      commit("toggleHelpDeskDialog");
    },
    updateImageCacheKey({ commit }) {
      commit("updateImageCacheKey");
    },
    async loadNotice({ commit, getters }) {
      let response;
      if (getters.isLoggedIn && getters.getUser?.UserID) {
        response = await dataService.GetNotice(getters.getUser.UserID, getters.isLoggedIn);
      } else {
        response = await dataService.GetNotice();
      }
   
      if(response.Result) {
        commit('setNotice', response.Data);
      } else {
        console.log(response.ErrorMessage);
      }
    },
    async loadISINStatusInfo({ commit, state, getters }) {
      let data;

      if (getters.isLoggedIn) { 
        data = await dataService.Get4Info(state.user.UserID, state.token, ''); 
      } 
      else if (cookies.isKey('userID') && !getters.isLoggedIn) {
        const cookieUserID = parseInt(cookies.get('userID'));
        data = await dataService.Get4Info('', '', cookieUserID);
      } 
      else data = await dataService.Get4Info('', '', '');

      commit('setISINStatusInfo', data);
    },
    async loadOpenGames({ commit }) {
      const data = await dataService.GetOpenGame();
      commit('setOpenGame', data);
    },
    async loadOpenGameDetail({ commit }, gameID) {
      const data = await dataService.GetOpenGameDetail(gameID);
      commit('setOpenGameDetail', { ...(data || {}), GameID: gameID });
      commit('OPEN_MODAL', {
        title: data?.GameName || 'Detail hry',
        view: 'GameDetail'
      });
    },
    async loadRunningGames({ commit }) {
      const data = await dataService.GetRunningGame();
      commit('setRunningGame', data);
    },
    async loadRewardDetail({ commit }, rewardID) {
      const data = await dataService.GetRewardDetail(rewardID);
      commit('setRewardDetail', { ...(data || {}), RewardID: rewardID });
      commit('OPEN_MODAL', { 
        view: 'RewardDetail' 
      });
    },
    async fetchISINChartData({ commit, getters }, payload) {
      const share = asIsinShare(payload);
      if (!share) return;
      commit('OPEN_ISIN_CHART_MODAL', share);
      try {
        const userID = getters.isLoggedIn ? getters.getUser?.UserID : undefined;
        const response = await dataService.GetISINChart(String(share.ISINID), userID);
        if (response.Result) {
          const title = response.Data?.Title || {};
          commit('SET_ISIN_CHART_MODAL', {
            share: {
              ...share,
              Nm: share.Nm || title.Name || "",
              ISIN: share.ISIN || title.ISIN || "",
              Clr: share.Clr || title.Color || ""
            },
            data: response,
            loading: false
          });
          commit('setISINChartData', response);
        } else {
          commit('CLOSE_ISIN_CHART_MODAL');
          swal.fire({
            position: 'center',
            icon: 'error',
            html: `${(response.ErrorMessage || ['Graf se nepodařilo načíst']).map(message => `<h5>${message}</h5>`).join('')}`,
            showConfirmButton: false,
            timer: 5000,
          });
        }
      } catch (error) {
        console.error(error);
        commit('CLOSE_ISIN_CHART_MODAL');
      }
    },
    closeIsinChartModal({ commit }) {
      commit('CLOSE_ISIN_CHART_MODAL');
    },
    closeTopOverlay({ state, commit }) {
      if (state.lightbox?.isOpen) return false;
      if (state.showAuthModal) return false;
      const stack = [];
      if (state.forum?.isOpen) {
        stack.push({ key: "forum", z: Number(state.forum.zIndex) || 0 });
      }
      if (state.isinChartModal?.isOpen) {
        stack.push({ key: "chart", z: Number(state.isinChartModal.zIndex) || 0 });
      }
      if (!stack.length) return false;
      stack.sort((a, b) => b.z - a.z);
      if (stack[0].key === "forum") commit("CLOSE_FORUM");
      else commit("CLOSE_ISIN_CHART_MODAL");
      return true;
    },
    addToGameHandler({ commit, state, getters }, payload) {
      commit('SET_PENDING_ADD_TO_GAME', payload);
      if (getters.isLoggedIn) {
        state.GameDetail = payload;
        commit('SET_PENDING_ADD_TO_GAME', null);
        commit('OPEN_MODAL', {
          title: 'Registrace do hry',
          view: 'AddToGame'
        });
      } else if (!state.showAuthModal) {
        commit('toggleLoginForm');
      }
    },
    resumePendingAddToGame({ commit, state }) {
      const pending = state.pendingAddToGame;
      if (!pending) return;
      state.GameDetail = pending;
      commit('SET_PENDING_ADD_TO_GAME', null);
      commit('OPEN_MODAL', {
        title: 'Registrace do hry',
        view: 'AddToGame'
      });
    },

    /* ============= Authentication ============= */
    async applyUserDarkMode({ commit, getters, state }) {
      const userID = getters.getUser?.UserID;
      const token = state.token;
      if (!userID || !token) return;
      try {
        const response = await dataService.GetUserSetting(userID, token);
        const settings = Array.isArray(response?.Data) ? response.Data : [];
        const dark = settings.find(isDarkSettingItem);
        if (!dark) return;
        if (dark.UserSettingTypeID) commit("setDarkSettingTypeId", dark.UserSettingTypeID);
        const parsed = parseDarkSettingValue(dark.UserSettingValue);
        if (parsed !== null) commit("setDarkMode", parsed);
      } catch (e) {
        console.error(e);
      }
    },
    async persistDarkModeSetting({ state, getters, commit }, isDark) {
      const userID = getters.getUser?.UserID;
      const token = state.token;
      if (!userID || !token) return true;
      try {
        let typeId = state.darkSettingTypeId;
        if (!typeId) {
          const response = await dataService.GetUserSetting(userID, token);
          const settings = Array.isArray(response?.Data) ? response.Data : [];
          const dark = settings.find(isDarkSettingItem);
          typeId = dark?.UserSettingTypeID || null;
          if (typeId) commit("setDarkSettingTypeId", typeId);
        }
        if (!typeId) return false;
        const response = await dataService.SetUserSetting(userID, token, {
          UserSettingTypeID: typeId,
          UserSettingValue: isDark ? "Y" : "N"
        });
        return !!response?.Result;
      } catch (e) {
        console.error(e);
        return false;
      }
    },
    async setThemeMode({ commit, dispatch, state }, isDark) {
      const previous = state.isDarkMode;
      const next = !!isDark;
      commit("setDarkMode", next);
      const ok = await dispatch("persistDarkModeSetting", next);
      if (!ok) commit("setDarkMode", previous);
      return ok;
    },
    async login({ commit, dispatch }, { token, user }) {
      commit('SET_USER', user);
      commit('SET_TOKEN', token);
      await dispatch('applyUserDarkMode');
      dispatch('resumePendingAddToGame');
    },
    logout: async ({ commit, state }, payload = {}) => {
      router.replace({path: '/'})
      try {
        if (!payload.skipApi && state.user?.UserID && state.token) {
          await authService.Logout(state.user.UserID, state.token);
        }
      } catch (e) {
        /* session už nemusí existovat */
      }
      commit('CLOSE_MODAL');
      commit('REMOVE_TOKEN');
      commit('REMOVE_USER');
    },
    sessionExpired: async ({ commit, dispatch }) => {
      commit('REMOVE_TOKEN');
      commit('REMOVE_USER');
      commit('CLOSE_MODAL');
      commit('CLOSE_FORUM');
      commit('CLOSE_LIGHTBOX');
      commit('OPEN_LOGIN_MODAL');
      dispatch('alertError', ['Přihlášení vypršelo. Přihlas se prosím znovu.']);
      if (router.currentRoute.value?.meta?.requiresAuth) {
        router.replace({ path: '/' });
      }
    },
    /* ============= Modal - popup window ============= */
    openModal({ commit }, payload) {
      commit('OPEN_MODAL', payload);
    },
    closeModal({ commit }) {
      commit('CLOSE_MODAL');
    },
    openForum({ commit, dispatch }, payload) {
      commit('OPEN_FORUM', payload);
      dispatch('loadForum');
    },
    closeForum({ commit }) {
      commit('CLOSE_FORUM');
    },
    openLightbox({ commit }, payload) {
      const sources = Array.isArray(payload?.sources) ? payload.sources.filter(Boolean) : [];
      if (!payload?.src && !sources.length) return;
      commit('OPEN_LIGHTBOX', payload);
    },
    stepLightbox({ commit }, delta) {
      commit('STEP_LIGHTBOX', delta);
    },
    closeLightbox({ commit }) {
      commit('CLOSE_LIGHTBOX');
    },
    async loadForum({ commit, state, getters }) {
      const ctx = state.forum.context || {};
      const payload = {};
      if (ctx.ISINID) payload.ISINID = ctx.ISINID;
      if (ctx.GameID) payload.GameID = ctx.GameID;
      if (ctx.RewardID) payload.RewardID = ctx.RewardID;
      if (!payload.ISINID && !payload.GameID && !payload.RewardID) {
        commit('SET_FORUM_POSTS', []);
        return;
      }
      if (getters.isLoggedIn && getters.getUser?.UserID) {
        payload.SessionUserID = getters.getUser.UserID;
        payload.SessionHash = state.token;
      }
      commit('SET_FORUM_LOADING', true);
      try {
        const response = await dataService.GetChat(payload);
        const rows = response?.Data ?? [];
        const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
        commit('SET_FORUM_POSTS', list);
      } catch (error) {
        console.error(error);
        commit('SET_FORUM_POSTS', []);
      }
    },
    async sendForumPost({ dispatch, state, getters }, chatText) {
      const text = String(chatText || '').trim();
      if (text.length < 5) {
        return { Result: false, ErrorMessage: ['Není zadána zpráva'] };
      }
      const ctx = state.forum.context || {};
      const payload = { ChatText: text };
      if (ctx.ISINID) payload.ISINID = ctx.ISINID;
      if (ctx.GameID) payload.GameID = ctx.GameID;
      if (ctx.RewardID) payload.RewardID = ctx.RewardID;
      if (getters.isLoggedIn && getters.getUser?.UserID) {
        payload.SessionUserID = getters.getUser.UserID;
        payload.SessionHash = state.token;
      }
      const response = await dataService.SendChat(payload);
      if (response?.Result) {
        await dispatch('loadForum');
      }
      return response;
    },
    /* ============= Alert ============= */
    alertSuccess({ commit }, message) {
      commit('alertSuccess', message);
    },
    alertError({ commit }, message) {
        commit('alertError', message);
        commit('alertClear');
    },
    alertClear({ commit }) {
        commit('alertClear');
    },
  },
  getters: {
    /* ============= Authentication ============= */
    isLoggedIn: state => {
      return state.token;
    },
    getUser: state => {
      return state.user;
    },
    isAnimator: state => {
      const v = state.user?.UserAnimator;
      return v === 1 || v === "1" || v === true;
    },

    /* ============= Numbers Formating ============= */
    formatNumber: () => (price) => {
      const formatter = new Intl.NumberFormat('cs-CZ', {
        style: 'currency',
        currency: 'CZK',
        minimumFractionDigits: 2
      });
      return formatter.format(price).replace("Kč", "CZK");
      //return Number(price).toLocaleString("cs-CZ", {style: "currency", currency: "CZK", minimumFractionDigits: 2});
    },
    formatTableNumber: () => (number) => {
      if (number == null || number === '') return '';
      const formatter = new Intl.NumberFormat('cs-CZ', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return formatter.format(number);
    },
    formatChangeTypeNumber: () => (number) => {
      if (number == null || number === '') return '';
      const formatter = new Intl.NumberFormat('cs-CZ', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      return (parseFloat(number) > 0) ? '+' + formatter.format(number) : formatter.format(number);
    },
  },
  modules: {
    loadingModule
  }
});
